/**
 * Platform settings editor. Reads + writes the operator-tunable values that
 * used to be hardcoded in the source (settlement boundary hours, enabled
 * payment methods, default fee rates, etc).
 *
 * Editing semantics:
 *  - The current effective value is shown plus a clear marker when an
 *    override exists vs when we're using the code default.
 *  - "Save" PATCHes /api/settings/:key. Value is validated server-side
 *    against the setting's zod schema; bad input shows a 400 with the reason.
 *  - "Reset to default" DELETEs the override row; reads after that fall
 *    back to the code constant.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Loader,
  MultiSelect,
  NumberInput,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconArrowBack,
  IconCheck,
  IconRefresh,
  IconSettings,
} from '@tabler/icons-react';
import { settingsApi, type SettingDto } from '../api/settings';
import { usePermissions } from '../auth/SessionContext';
import { describeError } from '../api/client';
import { formatGmt7 } from '../lib/format';
import { PAYOK_PAYMENT_METHODS } from '../api/types';
import { useTranslation } from '../i18n/LocaleContext';
import { JsonView } from '../components/JsonView';
import type { TranslationKey } from '../i18n/translations';
import { SettlementExceptionEditor } from '../components/SettlementExceptionEditor';

function CategoryLabel({ category }: { category: string }) {
  const { t } = useTranslation();
  const meta: Record<string, { labelKey: TranslationKey; color: string }> = {
    settlement: { labelKey: 'settings.catSettlement', color: 'grape' },
    settlement_exception: { labelKey: 'settings.catSettlementException', color: 'grape' },
    payment_methods: { labelKey: 'settings.catPaymentMethods', color: 'brand' },
    fees: { labelKey: 'settings.catFees', color: 'blue' },
    auth: { labelKey: 'settings.catAuth', color: 'red' },
    webhook: { labelKey: 'settings.catWebhook', color: 'teal' },
  };
  const m = meta[category];
  return (
    <Badge color={m?.color ?? 'gray'} variant="filled" size="md">
      {m ? t(m.labelKey) : category}
    </Badge>
  );
}

export function SettingsPage() {
  const { t } = useTranslation();
  // Platform settings are SUPERADMIN ONLY (the backend enforces requireSuperuser).
  // Skip the fetch and show a clear message for anyone else who reaches this URL.
  const { isSuperuser } = usePermissions();
  const query = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.list(),
    enabled: isSuperuser,
  });

  const grouped = useMemo(() => {
    // Defensive: if the proxy or backend returns a malformed shape (e.g.
    // nginx fell through to the SPA fallback because /api/settings wasn't
    // routed), data.rows can be undefined or the literal HTML string. Don't
    // crash the page — show empty groups; the error Alert above surfaces
    // the actual failure to the user.
    const rows = Array.isArray(query.data?.rows) ? query.data.rows : [];
    const m = new Map<string, SettingDto[]>();
    for (const s of rows) {
      const arr = m.get(s.category) ?? [];
      arr.push(s);
      m.set(s.category, arr);
    }
    return [...m.entries()];
  }, [query.data]);

  if (!isSuperuser) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />}>
        {t('settings.superuserOnly')}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>{t('settings.title')}</Title>
          <Text c="dimmed" size="sm">
            {t('settings.subtitle')}
          </Text>
        </div>
        <Tooltip label={t('settings.reload')}>
          <ActionIcon variant="default" onClick={() => void query.refetch()} loading={query.isRefetching}>
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {query.isLoading && (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      )}
      {query.error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {describeError(query.error, t('settings.failedToLoad'))}
        </Alert>
      )}

      {grouped.map(([cat, items]) => (
        <Card withBorder key={cat} padding={0}>
          <Group
            px="md"
            py="sm"
            style={{
              background:
                'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))',
              borderBottom:
                '1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4))',
            }}
          >
            <CategoryLabel category={cat} />
            <Text size="xs" c="dimmed">
              {items.length === 1
                ? t('settings.countSingular', { n: items.length })
                : t('settings.countPlural', { n: items.length })}
            </Text>
          </Group>
          <Stack gap={0}>
            {items.map((s, idx) => (
              <SettingRow key={s.key} setting={s} bordered={idx > 0} />
            ))}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}

function SettingRow({ setting, bordered }: { setting: SettingDto; bordered: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<unknown>(setting.value);
  // Reset the local draft whenever the upstream value changes (e.g. after save).
  // Using JSON.stringify for shallow change detection on object/array shapes.
  const upstreamSig = JSON.stringify(setting.value);
  const [lastSig, setLastSig] = useState(upstreamSig);
  if (lastSig !== upstreamSig) {
    setLastSig(upstreamSig);
    setDraft(setting.value);
  }

  const saveMut = useMutation({
    meta: { successKey: 'notify.setting.save.ok', errorKey: 'notify.setting.save.fail' },
    mutationFn: () => settingsApi.set(setting.key, draft),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
  });
  const resetMut = useMutation({
    meta: { successKey: 'notify.setting.reset.ok', errorKey: 'notify.setting.reset.fail' },
    mutationFn: () => settingsApi.reset(setting.key),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
  });

  const dirty = JSON.stringify(draft) !== upstreamSig;

  return (
    <Stack
      gap="xs"
      px="md"
      py="md"
      style={{
        borderTop: bordered
          ? '1px solid light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-4))'
          : undefined,
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div style={{ flex: 1 }}>
          <Group gap={6}>
            <Text fw={600} size="sm">{setting.name}</Text>
            {setting.isOverridden ? (
              <Badge color="blue" variant="light" size="xs">
                {t('settings.overrideBadge')}
              </Badge>
            ) : (
              <Badge color="gray" variant="light" size="xs">
                {t('settings.defaultBadge')}
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed" ff="monospace">{setting.key}</Text>
          <Text size="xs" c="dimmed" mt={4}>{setting.description}</Text>
        </div>
      </Group>

      <SettingEditor setting={setting} value={draft} onChange={setDraft} />

      {saveMut.isSuccess && !dirty && (
        <Alert color="green" icon={<IconCheck size={14} />} variant="light">
          {t('settings.savedAlert')}
        </Alert>
      )}

      <Group justify="space-between">
        <Group gap={6}>
          <Text size="xs" c="dimmed">
            {t('settings.defaultLabel')} <Code style={{ fontSize: 11 }}>{JSON.stringify(setting.default)}</Code>
          </Text>
          {setting.updatedAt && (
            <Text size="xs" c="dimmed">
              {t('settings.lastChanged', { user: setting.updatedBy ?? '', at: formatGmt7(setting.updatedAt) })}
            </Text>
          )}
        </Group>
        <Group gap="xs">
          {setting.isOverridden && (
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconArrowBack size={12} />}
              loading={resetMut.isPending}
              onClick={() => resetMut.mutate()}
            >
              {t('settings.resetToDefault')}
            </Button>
          )}
          <Button
            size="xs"
            leftSection={<IconSettings size={12} />}
            disabled={!dirty}
            loading={saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {t('common.save')}
          </Button>
        </Group>
      </Group>
    </Stack>
  );
}

type SettlementWindow = { delayDays: number; hour: number };

/**
 * Editor for one settlement window (`settlement.window_afternoon` /
 * `settlement.window_morning`): a day offset + an hour, with a plain-language
 * preview of what the pair means. `minSameDayHour` only applies while the day
 * offset is 0; raising the offset lifts it, lowering it back clamps the hour.
 */
function SettlementWindowEditor({
  value,
  onChange,
  fallback,
  minDelayDays,
  minSameDayHour,
}: {
  value: unknown;
  onChange: (v: SettlementWindow) => void;
  fallback: SettlementWindow;
  minDelayDays: number;
  minSameDayHour: number;
}) {
  const { t } = useTranslation();
  const v = (value && typeof value === 'object' ? value : {}) as Partial<SettlementWindow>;
  const delayDays = typeof v.delayDays === 'number' ? v.delayDays : fallback.delayDays;
  const hour = typeof v.hour === 'number' ? v.hour : fallback.hour;
  const minHour = delayDays === 0 ? minSameDayHour : 0;
  const hh = String(hour).padStart(2, '0');

  return (
    <Stack gap={6}>
      <Group grow align="flex-start">
        <NumberInput
          label={t('settings.settlementWindowDaysLabel')}
          description={t('settings.settlementWindowDaysDesc')}
          min={minDelayDays}
          max={31}
          allowDecimal={false}
          value={delayDays}
          onChange={(n) => {
            const next = typeof n === 'number' ? n : fallback.delayDays;
            onChange({
              delayDays: next,
              hour: next === 0 ? Math.max(hour, minSameDayHour) : hour,
            });
          }}
        />
        <NumberInput
          label={t('settings.settlementWindowHourLabel')}
          description={t('settings.settlementWindowHourDesc', { min: minHour })}
          min={minHour}
          max={23}
          allowDecimal={false}
          value={hour}
          onChange={(n) => onChange({ delayDays, hour: typeof n === 'number' ? n : fallback.hour })}
        />
      </Group>
      <Text size="xs" c="dimmed">
        {delayDays === 0
          ? t('settings.settlementWindowPreviewSameDay', { hour: hh })
          : delayDays === 1
            ? t('settings.settlementWindowPreviewNextDay', { hour: hh })
            : t('settings.settlementWindowPreviewDays', { n: delayDays, hour: hh })}
      </Text>
    </Stack>
  );
}

/**
 * Renders an input matched to the setting's shape:
 *   - settlement.window_afternoon / settlement.window_morning → SettlementWindowEditor
 *   - settlement.custom_exceptions → SettlementExceptionEditor (checkboxes + calendar)
 *   - payment_methods.enabled → MultiSelect from PAYOK_PAYMENT_METHODS
 *   - fees.*_default → NumberInput
 * Falls back to a read-only JSON view for unknown shapes.
 */
function SettingEditor({
  setting,
  value,
  onChange,
}: {
  setting: SettingDto;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const { t } = useTranslation();
  if (setting.key === 'settlement.window_afternoon') {
    // Day offset min 1: this window runs until 23:59, so a same-day hour would
    // already have passed for late payments (mirrors the zod refine).
    return (
      <SettlementWindowEditor
        value={value}
        onChange={onChange}
        fallback={{ delayDays: 1, hour: 13 }}
        minDelayDays={1}
        minSameDayHour={0}
      />
    );
  }
  if (setting.key === 'settlement.window_morning') {
    // Same day (offset 0) needs hour >= 12: the window closes at 12:00, so an
    // earlier hour would stamp a settlement time that has already passed.
    return (
      <SettlementWindowEditor
        value={value}
        onChange={onChange}
        fallback={{ delayDays: 0, hour: 19 }}
        minDelayDays={0}
        minSameDayHour={12}
      />
    );
  }
  if (setting.key === 'settlement.custom_exceptions') {
    return <SettlementExceptionEditor value={value} onChange={onChange} />;
  }
  if (setting.key === 'payment_methods.enabled') {
    return (
      <MultiSelect
        label={t('settings.enabledMethodsLabel')}
        data={[...PAYOK_PAYMENT_METHODS]}
        value={Array.isArray(value) ? (value as string[]) : []}
        onChange={(v) => onChange(v)}
        searchable
      />
    );
  }
  if (setting.key === 'fees.deposit_bps_default') {
    return (
      <NumberInput
        label={t('settings.basisPointsLabel')}
        description={t('settings.basisPointsDesc')}
        min={0}
        max={10_000}
        value={typeof value === 'number' ? value : 0}
        onChange={(v) => onChange(typeof v === 'number' ? v : 0)}
      />
    );
  }
  if (setting.key === 'fees.disbursement_idr_default') {
    return (
      <NumberInput
        label={t('settings.idrLabel')}
        thousandSeparator=","
        min={0}
        max={999_999_999}
        value={typeof value === 'number' ? value : 0}
        onChange={(v) => onChange(typeof v === 'number' ? v : 0)}
      />
    );
  }
  // Generic fallback: show as JSON. Read-only because we don't know the shape.
  return (
    <Alert color="yellow" variant="light" icon={<IconAlertCircle size={14} />}>
      <Text size="xs">
        {t('settings.noEditorTitle')}
      </Text>
      <div style={{ marginTop: 4 }}>
        <JsonView value={value} maxHeight={240} />
      </div>
    </Alert>
  );
}
